import { Injectable } from '@angular/core';
import { TVDLocationDetailsRowData, TVDLocationServicesDetailsRowData, TVDProductDetailsRow, TVDTableHeader} from 'src/app/features/total-value-delivery/types/tvd-types';
import { CapitalizePipe } from "src/app/core/pipes/capitalize.pipe";

@Injectable({
  providedIn: 'root',
})
export class TVDDetailsConfigService {

    private capitalizePipe = new CapitalizePipe();

    constructor() {}

    // #region Table Headers
    setTableHeaderProductColumns(metricUnits: string) : TVDTableHeader[] {
        return [
        {
          title: "SKU",
          sortBy: "productName",
          neptuneCssClass:'w-[15%]',
          sortDirection: "desc",
        },
        {
          title: "Product Category",
          sortBy: "solution",
          neptuneCssClass:'w-[15%]',
          sortDirection: "desc",
        },
        {
          title: "% Of Total",
          sortBy: "percentOfTotal",
          neptuneCssClass:'w-[10%]',
          sortDirection: "desc",
        },
        {
          title: "# of Locations",
          sortBy: "numberOfLocations",
          neptuneCssClass:'w-[10%]',
          sortDirection: "desc",
        },
        {
          title: "Sales Quantity",
          sortBy: "quantityPurchased",
          neptuneCssClass:'w-[10%]',
          sortDirection : "desc"
        },
        {
          title: "Estimated Total Value",
          sortBy: "estimatedMetricDollars",
          neptuneCssClass:'w-[15%]',
          sortDirection: "desc",
        },
        {
          title: metricUnits
              ? this.capitalizePipe.transform(metricUnits)
              : "",
          sortBy: "estimatedMetricQuantity",
          neptuneCssClass:'w-[20%]',
          sortDirection: "desc",
          },
        ];
    }

    setTableHeaderLocationColumns(metricUnits : string) : TVDTableHeader[] {
        return [
        {
          title: "Site Info",
          sortBy: "siteName",
          neptuneCssClass:'w-[30%]',
          sortDirection: "desc" ,
        },
        {
          title: "# Contributing Products ",
          sortBy: "contributingProducts",
          neptuneCssClass:'w-[15%]',
          sortDirection: "desc",
        },
        {
          title: "% Of Total",
          sortBy: "percentOfTotal",
          neptuneCssClass:'w-[15%]',
          sortDirection: "desc",
        },
        {
          title: "Estimated Total Value",
          sortBy: "estimatedMetricDollars",
          neptuneCssClass:'w-[15%]',
          sortDirection: "desc",
        },
        {
          title: metricUnits
          ? this.capitalizePipe.transform(metricUnits)
          : "",
          sortBy: "estimatedMetricQuantity",
          neptuneCssClass:'w-[25%]',
          sortDirection: "desc",
        },
      ];
    }

    setTableHeaderLocationServicesColumns(metricUnits: string, metricName:string) : TVDTableHeader[] {
        let headers= [
        {
          title: "Site Info",
          sortBy: "serviceAccountName",
          neptuneCssClass: !this.hasTwoMetricQuantities(metricName) ? "w-[30%]" : "w-[25%]",
          sortDirection: "desc" ,
        },
        {
          title: "% Of Total TVD",
          sortBy: "percentOfTotal",
          neptuneCssClass: !this.hasTwoMetricQuantities(metricName) ? "w-[20%]" : "w-[15%]",
          sortDirection: "desc",
        },
        {
          title: "Estimated Total Value",
          sortBy: "estimatedMetricDollars",
          neptuneCssClass: !this.hasTwoMetricQuantities(metricName) ? "w-[25%]" :"w-[20%]",
          sortDirection: "desc",
        },
        {
          title: metricUnits && metricUnits !== 'kWh' //dont capitalize when the metricUnit is kWh
          ? this.capitalizePipe.transform(`# ${metricUnits}`)
          : `# ${metricUnits}`,
          sortBy: "estimatedMetricQuantity",
          neptuneCssClass: !this.hasTwoMetricQuantities(metricName) ? "w-[25%] estd-metric" :"w-[20%] estd-metric",
          sortDirection: "desc",
        }
        ];

        if(this.hasTwoMetricQuantities(metricName)){
            let secondaryMetricHeader = this.getSecondaryMetricUnits(metricName);
            headers = headers.concat([
            {
              title: secondaryMetricHeader
              ? this.capitalizePipe.transform(secondaryMetricHeader)
              : "",
              sortBy: "estimatedSecondaryMetricQuantity",
              neptuneCssClass:'w-[20%] secondary-estd-metric',
              sortDirection: "desc",
            }]);
        }
        return headers as TVDTableHeader[];
    }
    // #endregion


    // #region Excel Format
    formatPestExcelData(tvdLocationServicesRowData: TVDLocationServicesDetailsRowData[] | undefined, metricUnit: string | undefined, metricName: string, multiplier2: number) : {data:any[], columns:string[]}{
        let data : any[] = [];
        let columns : string[] = [];

        if(this.hasTwoMetricQuantities(metricName)){
          data = tvdLocationServicesRowData?.map(row => {
            return {
              'Service Account Id': row.serviceAccountId,
              'Service Account Name': row.serviceAccountName,
              'Site Address Line 1': row.streetAddress,
              'Site City': row.city,
              'Site State': row.state,
              'Site Zip Code': row.zipCode,
              'Percent of Total': this.formatExcelNumberValue(row.percentOfTotal),
              'Estimated Total Value': this.formatExcelNumberValue(row.estimatedMetricDollars),
              'Metric Name': metricName,
              'Estimated Metric Quantity': this.formatExcelNumberValue(row.estimatedMetricQuantity),
              'Metric Units': (metricUnit || ''),
              'Estimated Secondary Metric Quantity': this.formatExcelNumberValue(row.estimatedSecondaryMetricQuantity),
              'Secondary Metric Units': this.getSecondaryMetricUnits(metricName)
            };
          }) || []; // Provide a default empty array if data is undefined

          columns = [
            'Service Account Id', 'Service Account Name', 'Site Address Line 1', 'Site City', 'Site State',
            'Site Zip Code', 'Percent of Total', 'Estimated Total Value',
            'Metric Name', 'Estimated Metric Quantity', 'Metric Units',
            'Estimated Secondary Metric Quantity', 'Secondary Metric Units'
          ];
        }else{

          data = tvdLocationServicesRowData?.map(row => {
            // If metric is 'Room Nights Saved' and costAssumptionValue2 > 0, multiply costAssumptionValue2 with estimatedMetricQuantity
            let estimatedMetricQuantity = row.estimatedMetricQuantity;
            if (multiplier2 > 0 && metricName == "Room Nights Saved") {
              estimatedMetricQuantity = multiplier2 * row.estimatedMetricQuantity;
            }
            return {
              'Service Account Id': row.serviceAccountId,
              'Service Account Name': row.serviceAccountName,
              'Site Address Line 1': row.streetAddress,
              'Site City': row.city,
              'Site State': row.state,
              'Site Zip Code': row.zipCode,
              'Percent of Total': this.formatExcelNumberValue(row.percentOfTotal),
              'Estimated Total Value': this.formatExcelNumberValue(row.estimatedMetricDollars),
              'Metric Name' : metricName,
              'Estimated Metric Quantity': this.formatExcelNumberValue(estimatedMetricQuantity),
              'Metric Units' : (metricUnit || ''),
            };
          }) || []; // Provide a default empty array if data is undefined

          columns = [
            'Service Account Id', 'Service Account Name', 'Site Address Line 1', 'Site City', 'Site State',
            'Site Zip Code', 'Percent of Total', 'Estimated Total Value',
            'Metric Name', 'Estimated Metric Quantity', 'Metric Units'
          ];
        }
        return {data, columns};
    }


    formatLocationExcelData(tvdLocationRowData: TVDLocationDetailsRowData[] | undefined, metricUnit: string | undefined, metricName: string | undefined, regions: string[] | undefined,isCostAssumptionApplied: boolean=false) : {data:any[], columns:string[]} {
        let data = tvdLocationRowData?.map(row => {
                  let formattedRow : any =  {
                    'H5, name and number': row.h5Number ? `${row.h5Name} ${row.h5Number}` : '',
                    'H7, name and number': row.h7Number ? `${row.h7Name} ${row.h7Number}` : '',
                    'Site Number': row.siteID,
                    'Site Name': row.siteName,
                    'Site Address Line 1': row.streetAddress,
                    'Site City': row.city,
                    'Site State': row.state,
                    'Site Country': row.countryCode,
                    'Site Zip Code': row.zipCode,
                    '# of Contributing Products': row.contributingProducts,
                    '% of Total': this.formatExcelNumberValue(row.percentOfTotal),
                    'Estimated Total Value': this.formatExcelNumberValue(isCostAssumptionApplied && row.modifiedEstimatedMetricDollars ? row.modifiedEstimatedMetricDollars : row.estimatedMetricDollars),
                    'Metric Name' : metricName,
                    'Estimated Metric Quantity': this.formatExcelNumberValue(row.estimatedMetricQuantity),
                    'Metric Units' : (metricUnit || ''),
                    'TR Name': row.trName,
                    'DM Name': row.dmName,
                    'AM Name': row.amName
                  };

                  return formattedRow;
                }) || []; // Provide a default empty array if  data is undefined

        let columns = [
            'H5, name and number', 'H7, name and number', 'Site Number', 'Site Name',
            'Site Address Line 1', 'Site City', 'Site State', 'Site Country',
            'Site Zip Code', '# of Contributing Products', '% of Total',
            'Estimated Total Value', 'Metric Name', 'Estimated Metric Quantity', 'Metric Units',
            'TR Name', 'DM Name', 'AM Name'
        ];

        return {data, columns};
    }

    formatProductExcelData(tvdProductsRowData: TVDProductDetailsRow[] | undefined, metricUnit: string | undefined, metricName: string | undefined,isCostAssumptionApplied: boolean=false) : {data:any[], columns:string[],} {
        let data = tvdProductsRowData?.map(row => {
          return {
            'Product sku/code': row.productCode,
            'Product name': row.productDesc,
            'Product Category': row.solution,
            'Percent of Total': this.formatExcelNumberValue(row.percentOfTotal),
            'Number of locations': row.numberOfLocations,
            'Sales Quantity': `${row.quantityPurchased} ${row.isEDPRental ? 'Months' : 'Cases'}`,
            'Estimated Total Value': this.formatExcelNumberValue(isCostAssumptionApplied && row.modifiedEstimatedMetricDollars ? row.modifiedEstimatedMetricDollars : row.estimatedMetricDollars),
            'Metric Name' : metricName,
            'Estimated Metric Quantity': this.formatExcelNumberValue(row.estimatedMetricQuantity),
            'Metric Units' : (metricUnit || ''),
          };
        }) || []; // Provide a default empty array if data is undefined

        let columns = [
          'Product sku/code', 'Product name', 'Product Category', 'Percent of Total',
          'Number of locations', 'Sales Quantity', 'Estimated Total Value', 'Metric Name', 'Estimated Metric Quantity', 'Metric Units'
        ];
        return {data, columns};
    }


    //Format numbers fr business for excel spreadsheet
    //If value >=1, round to an integer
    //If between 0 and 1, round to 2 decimal places, unless it is 0.00, in which case return full decimal value
    formatExcelNumberValue(value: number | undefined):number{
        if(value === undefined || value === null){
          return 0;
        }

        if(value < 1 && value > 0){
          if(value.toFixed(2)==="0.00"){
            return value;
          }
          return Number(value.toFixed(2));
        }
        return Math.round(value);
    }

    // #endregion


    /*
    * These two Pest Metrics have an additional metric, so need to add an extra column in the table
    */
    hasTwoMetricQuantities(metricName : string){
        return metricName === 'No Charge Additional Services' || metricName === 'Set-up Services';
    }

    hasTwoMultipliers(metricName: string): boolean {
        return metricName === 'Room Nights Saved' || metricName === 'Set-up Services';
    }

    isRetrievalRequiredFromAPI(metricName: string): boolean {
      return metricName === 'No Charge Additional Services' || metricName === 'Set-up Services' || metricName === 'Furniture Preservation (Energy)';
    }

    getAvgMetricUnitsForPest(metricName: string){
        switch(metricName){
        case 'Set-up Services':
        case 'No Charge Additional Services':
            return 'PE CV $ per No Charge Service';
        case 'General Contractor Cost Avoidance':
            return 'Base $ per Exclusion Service';
        case 'Pest Prevention Findings':
            return 'Value per Finding';
        case 'Room Nights Saved':
            return 'Value per Night';
        case 'Furniture Preservation (Assets)':
            return 'Value per Set';
        case 'Furniture Preservation (Energy)':
            return 'Value per kWh';
        default:
            return '';
        }
    }

  // For table/column header formatting
  private getSecondaryMetricUnits(metricName: string){
    //if not 'No Charge Additional Services', then it is 'Set-up Services'
    return metricName === 'No Charge Additional Services' ? "# Rodent No Charge Services" : "$ Value Invoiced";
  }


}
